/**
 * AI Generator configurations for different modules
 * Each module defines:
 * - contextFields: Fields that can be sent as context
 * - generatableFields: Fields that can be generated
 * - options: Available options for generation
 */

export const aiGeneratorConfigs = {
  hse_document: {
    module: 'hse_document',
    buttonLabel: 'Generer innhold med AI',
    icon: 'SparklesIcon',
    contextFields: {
      title: { label: 'Tittel', type: 'string', description: 'Dokumenttittel' },
      description: { label: 'Beskrivelse', type: 'string', description: 'Beskrivelse av dokumentet' },
      discipline: { label: 'Fag/Bransje', type: 'string', description: 'Fag eller bransje' },
      category: { label: 'Kategori', type: 'string', description: 'Kategori' },
      chapter: { label: 'Kapittel', type: 'string', description: 'Kapittelnummer' },
      document_type: { label: 'Dokumenttype', type: 'string', description: 'Type dokument' },
      existing_structure: {
        label: 'Nåværende struktur',
        type: 'string',
        description: 'Fag/Bransje → Kategori → Kapittel (for bulk JSON)'
      },
      existing_documents: {
        label: 'Eksisterende dokumenter',
        type: 'string',
        description: 'Tittel/type/kapittel — unngå duplikater'
      }
    },
    generatableFields: {
      content: { 
        label: 'Innhold', 
        type: 'html',
        description: 'Generer dokumentinnhold basert på kontekst',
        default: true
      },
      description: {
        label: 'Beskrivelse',
        type: 'string',
        description: 'Generer en beskrivelse av dokumentet',
        default: false
      },
      documents_json: {
        label: 'Dokumenter (JSON bulk)',
        type: 'json',
        description: 'Generer bulk-JSON for dokumenter + valgfri ny struktur',
        default: false
      }
    },
    options: {
      language: {
        label: 'Språk',
        type: 'select',
        options: [
          { value: 'no', label: 'Norsk' },
          { value: 'en', label: 'Engelsk' }
        ],
        default: 'no'
      },
      tone: {
        label: 'Tone',
        type: 'select',
        options: [
          { value: 'professional', label: 'Profesjonell' },
          { value: 'formal', label: 'Formell' },
          { value: 'casual', label: 'Uformell' }
        ],
        default: 'professional'
      },
      length: {
        label: 'Lengde',
        type: 'select',
        options: [
          { value: 'short', label: 'Kort' },
          { value: 'medium', label: 'Middels' },
          { value: 'detailed', label: 'Detaljert' }
        ],
        default: 'detailed'
      },
      html_format: {
        label: 'Format',
        type: 'select',
        options: [
          { value: 'html', label: 'HTML (med formatering)' },
          { value: 'text', label: 'Ren tekst' }
        ],
        default: 'html'
      },
      document_count: {
        label: 'Antall dokumenter (bulk)',
        type: 'select',
        options: [
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: '3', label: '3' },
          { value: '5', label: '5' }
        ],
        default: '2'
      },
      user_goal: {
        label: 'Egne instruksjoner (valgfritt)',
        type: 'text',
        multiline: true,
        default: '',
        description: 'F.eks. «lag 3 instrukser for varmt arbeid»'
      }
    }
  },
  // Bulk JSON authoring entry (same API module, documents_json only)
  hse_document_bulk: {
    module: 'hse_document',
    buttonLabel: 'Generer dokumenter (JSON) med AI',
    icon: 'SparklesIcon',
    contextFields: {
      title: { label: 'Tema / tittel', type: 'string', description: 'Hva skal genereres' },
      description: { label: 'Mål / omfang', type: 'string', description: 'Kort beskrivelse' },
      discipline: { label: 'Fag/Bransje', type: 'string', description: 'Hint' },
      category: { label: 'Kategori', type: 'string', description: 'Hint' },
      chapter: { label: 'Kapittel', type: 'string', description: 'Hint' },
      document_type: { label: 'Dokumenttype', type: 'string', description: 'policy/procedure/instruction/…' },
      existing_structure: {
        label: 'Nåværende struktur',
        type: 'string',
        description: 'Fag → Kategori → Kapittel'
      },
      existing_documents: {
        label: 'Eksisterende dokumenter',
        type: 'string',
        description: 'Liste over dokumenter som allerede finnes (unngå duplikater)'
      }
    },
    generatableFields: {
      documents_json: {
        label: 'Dokumenter (JSON bulk)',
        type: 'json',
        description: 'Bulk-JSON for dokumenter + valgfri ny struktur',
        default: true
      }
    },
    options: {
      language: {
        label: 'Språk',
        type: 'select',
        options: [
          { value: 'no', label: 'Norsk' },
          { value: 'en', label: 'Engelsk' },
          { value: 'sv', label: 'Svensk' },
          { value: 'pl', label: 'Polsk' }
        ],
        default: 'no'
      },
      length: {
        label: 'Omfang per dokument',
        type: 'select',
        options: [
          { value: 'short', label: 'Kort' },
          { value: 'medium', label: 'Middels' },
          { value: 'detailed', label: 'Detaljert' }
        ],
        default: 'medium'
      },
      document_count: {
        label: 'Antall dokumenter',
        type: 'select',
        options: [
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: '3', label: '3' },
          { value: '5', label: '5' }
        ],
        default: '2'
      },
      user_goal: {
        label: 'Egne instruksjoner (valgfritt)',
        type: 'text',
        multiline: true,
        default: '',
        description: 'F.eks. «ny bransje Glass + 2 instrukser»'
      }
    }
  },
  webpage_page_builder: {
    module: "webpage_page_builder",
    buttonLabel: "Generer side (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      page_title: { label: "Sidetittel", type: "string", description: "Tittel som vises og brukes i kontekst" },
      page_slug: { label: "Slug", type: "string", description: "URL-fragment" },
      meta_description: { label: "Metabeskrivelse", type: "string", description: "SEO / sammendrag" }
    },
    generatableFields: {
      blocks_json: {
        label: "Sideblokker (JSON)",
        type: "json",
        description: "Genererer Page Builder JSON (blokker) basert på kontekst",
        default: true
      }
    },
    options: {
      language: {
        label: "Språk (tekst i blokker)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" }
        ],
        default: "no"
      },
      page_kind: {
        label: "Sidetype",
        type: "select",
        options: [
          { value: "landing", label: "Landing / kampanje" },
          { value: "content", label: "Innhold" },
          { value: "about", label: "Om oss" },
          { value: "contact", label: "Kontakt" }
        ],
        default: "content"
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" }
        ],
        default: "medium"
      },
      apply_mode: {
        label: "I editoren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt alle blokker" },
          { value: "append", label: "Legg til nederst" }
        ],
        default: "replace"
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. tonen, fokus, eller inkluder priser / team"
      }
    }
  },
  guide_flow: {
    module: "guide_flow",
    buttonLabel: "Generer guideflyt (JSON)",
    icon: "SparklesIcon",
    contextFields: {
      guide_title: { label: "Guidetittel", type: "string", description: "Navn på guiden" },
      guide_description: { label: "Beskrivelse", type: "string", description: "Hva guiden skal gjøre" },
      guide_kind: {
        label: "Guidetype (maskin lesbar)",
        type: "string",
        description: "case_onboarding eller field_report — styrer tillatte effekttyper"
      }
    },
    generatableFields: {
      flow_json: {
        label: "Guideflyt (JSON)",
        type: "json",
        description: "Komplett flyt: version, viewport, start_node_id, nodes",
        default: true
      }
    },
    options: {
      language: {
        label: "Språk (spørsmålstekster)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" }
        ],
        default: "no"
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" }
        ],
        default: "medium"
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "Ekstra krav til flyt, grener eller effekter"
      }
    }
  },
  project_task_template: {
    module: "project_task_template",
    buttonLabel: "Generer oppgavemal (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      set_title: { label: "Tittel", type: "string", description: "Navn på malsettet" },
      set_description: { label: "Beskrivelse", type: "string", description: "Når settet skal brukes" },
      set_category: { label: "Kategori", type: "string", description: "Valgfri gruppering" },
    },
    generatableFields: {
      set_json: {
        label: "Malsett (JSON)",
        type: "json",
        description: "Genererer malsett JSON (title, description, items, …)",
        default: true,
      },
    },
    options: {
      language: {
        label: "Språk (titler og beskrivelser)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" },
        ],
        default: "no",
      },
      template_purpose: {
        label: "Formål",
        type: "select",
        options: [
          { value: "kickoff", label: "Oppstart / nytt prosjekt" },
          { value: "quality", label: "KS / kvalitet" },
          { value: "billing", label: "Fakturering iht. plan" },
          { value: "handover", label: "Overtakelse / prosjektlukking" },
          { value: "service", label: "Serviceavtale / oppfølging" },
          { value: "hse", label: "HMS / SHA på prosjekt" },
        ],
        default: "kickoff",
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" },
        ],
        default: "medium",
      },
      apply_mode: {
        label: "I editoren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt alle oppgaver" },
          { value: "append", label: "Legg til nederst" },
        ],
        default: "replace",
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. antall oppgaver, KS-oppfølging eller faktureringsplan",
      },
    },
  },
  meeting_minutes: {
    module: "meeting_minutes",
    buttonLabel: "Generer agenda (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      minutes_title: { label: "Tittel", type: "string", description: "Navn på malen eller referatet" },
      minutes_description: { label: "Beskrivelse", type: "string", description: "Når agendaen skal brukes" },
      meeting_type: { label: "Møtetype", type: "string", description: "employee_interview, project_meeting eller other" },
      project_name: { label: "Prosjekt", type: "string", description: "Valgfritt prosjektnavn" },
      employee_name: { label: "Ansatt", type: "string", description: "Valgfritt ansattnavn" },
    },
    generatableFields: {
      agenda_json: {
        label: "Agenda (JSON)",
        type: "json",
        description: "Genererer møtereferat-agenda JSON (title, groups, entries)",
        default: true,
      },
    },
    options: {
      language: {
        label: "Språk (titler og innlegg)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" },
        ],
        default: "no",
      },
      meeting_purpose: {
        label: "Formål",
        type: "select",
        options: [
          { value: "employee_interview", label: "Medarbeidersamtale" },
          { value: "project_status", label: "Prosjektstatus" },
          { value: "kickoff", label: "Oppstartsmøte" },
          { value: "standup", label: "Tavlemøte / standup" },
          { value: "hse", label: "HMS / vernerunde" },
          { value: "other", label: "Annet møte" },
        ],
        default: "project_status",
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" },
        ],
        default: "medium",
      },
      apply_mode: {
        label: "I editoren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt alle punkter" },
          { value: "append", label: "Legg til nederst" },
        ],
        default: "replace",
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. antall punkter, HMS-fokus eller medarbeidersamtale",
      },
    },
  },
  offer_module_builder: {
    module: "offer_module_builder",
    buttonLabel: "Generer modulgraf (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      calculation_title: { label: "Kalkyletittel", type: "string", description: "Navn på kalkylen" },
      customer_hint: { label: "Kunde", type: "string", description: "Valgfritt kundenavn" },
      project_hint: { label: "Prosjekt", type: "string", description: "Valgfritt prosjektnavn" },
      existing_node_titles: {
        label: "Eksisterende noder",
        type: "string",
        description: "Titler på noder som allerede ligger på lerretet",
      },
    },
    generatableFields: {
      module_graph_json: {
        label: "Modulgraf (JSON)",
        type: "json",
        description: "Genererer module_graph JSON (nodes, edges, viewport)",
        default: true,
      },
    },
    options: {
      language: {
        label: "Språk (titler og notater)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" },
        ],
        default: "no",
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" },
        ],
        default: "medium",
      },
      apply_mode: {
        label: "I editoren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt hele grafen" },
          { value: "append", label: "Legg til på lerretet" },
        ],
        default: "replace",
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. «service leilighet + km + 12 % påslag + fordeling + total»",
      },
    },
  },
  checklist_type: {
    module: "checklist_type",
    buttonLabel: "Generer sjekklistetype (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      type_title: { label: "Tittel", type: "string", description: "Navn på sjekklistetypen" },
      type_description: {
        label: "Introduksjon",
        type: "string",
        description: "Kort beskrivelse vist ved utfylling",
      },
      discipline: { label: "Fag/bransje", type: "string", description: "Valgfritt fagområde" },
    },
    generatableFields: {
      type_json: {
        label: "Sjekklistetype (JSON)",
        type: "json",
        description: "Genererer sjekklistetype JSON (title, description, items, …)",
        default: true,
      },
    },
    options: {
      language: {
        label: "Språk (etiketter og veiledning)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" },
        ],
        default: "no",
      },
      checklist_purpose: {
        label: "Formål",
        type: "select",
        options: [
          { value: "inspection", label: "Inspeksjon / kontrollrunde" },
          { value: "safety", label: "HMS / sikkerhet" },
          { value: "quality", label: "Kvalitetskontroll" },
          { value: "maintenance", label: "Vedlikehold / service" },
          { value: "handover", label: "Overtakelse / ferdigstillelse" },
          { value: "vehicle", label: "Kjøretøy / utstyr" },
        ],
        default: "inspection",
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" },
        ],
        default: "medium",
      },
      apply_mode: {
        label: "I editoren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt alle punkter" },
          { value: "append", label: "Legg til nederst" },
        ],
        default: "replace",
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. antall punkter, HMS-krav eller målefelter",
      },
    },
  },
  document_form_template: {
    module: "document_form_template",
    buttonLabel: "Generer mal (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      template_title: { label: "Mal-tittel", type: "string", description: "Tittel på dokumentmalen" },
      template_description: { label: "Beskrivelse", type: "string", description: "Hva malen brukes til" },
      category: { label: "Kategori", type: "string", description: "Kategori / fagområde" }
    },
    generatableFields: {
      blocks_json: {
        label: "Malblokker (JSON)",
        type: "json",
        description: "Genererer Document Form JSON (title, description, blocks)",
        default: true
      }
    },
    options: {
      language: {
        label: "Språk (etiketter og tekst)",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" }
        ],
        default: "no"
      },
      form_purpose: {
        label: "Formål",
        type: "select",
        options: [
          { value: "declaration", label: "Erklæring / signert bekreftelse" },
          { value: "checklist", label: "Sjekkliste / inspeksjon" },
          { value: "meeting", label: "Møtereferat / vernerunde" },
          { value: "registration", label: "Registrering / generelt skjema" },
          { value: "incident", label: "Avvik / hendelse" }
        ],
        default: "registration"
      },
      length: {
        label: "Omfang",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" }
        ],
        default: "medium"
      },
      apply_mode: {
        label: "I malbyggeren",
        type: "select",
        options: [
          { value: "replace", label: "Erstatt alle blokker" },
          { value: "append", label: "Legg til nederst" }
        ],
        default: "replace"
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. påkrevde felt, HMS-krav eller spesifikke seksjoner"
      }
    }
  },
  job_opening: {
    module: "job_opening",
    buttonLabel: "Generer stilling (JSON) med AI",
    icon: "SparklesIcon",
    contextFields: {
      title: { label: "Tema / tittel", type: "string", description: "Hva skal genereres" },
      description: { label: "Mål / omfang", type: "string", description: "Kort beskrivelse" },
      location: { label: "Sted", type: "string", description: "Hint" },
      employment_type: { label: "Ansettelsesform", type: "string", description: "full_time / part_time / …" },
      department_name: { label: "Avdeling", type: "string", description: "Hint" },
      existing_openings: {
        label: "Eksisterende stillinger",
        type: "string",
        description: "Tittel/slug/status — unngå duplikater",
      },
    },
    generatableFields: {
      openings_json: {
        label: "Stillinger (JSON bulk)",
        type: "json",
        description: "Bulk-JSON for stillinger + valgfrie kandidater. Kvalifikasjoner som requirement_items [{id,text}].",
        default: true,
      },
    },
    options: {
      language: {
        label: "Språk",
        type: "select",
        options: [
          { value: "no", label: "Norsk" },
          { value: "en", label: "Engelsk" },
          { value: "sv", label: "Svensk" },
          { value: "pl", label: "Polsk" },
        ],
        default: "no",
      },
      length: {
        label: "Omfang per stilling",
        type: "select",
        options: [
          { value: "short", label: "Kort" },
          { value: "medium", label: "Middels" },
          { value: "detailed", label: "Detaljert" },
        ],
        default: "medium",
      },
      opening_count: {
        label: "Antall stillinger",
        type: "select",
        options: [
          { value: "1", label: "1" },
          { value: "2", label: "2" },
          { value: "3", label: "3" },
        ],
        default: "1",
      },
      include_applications: {
        label: "Inkluder testdata-kandidater",
        type: "select",
        options: [
          { value: "false", label: "Nei" },
          { value: "true", label: "Ja" },
        ],
        default: "false",
      },
      user_goal: {
        label: "Egne instruksjoner (valgfritt)",
        type: "text",
        multiline: true,
        default: "",
        description: "F.eks. «utkast til lærling ventilasjon»",
      },
    },
  },
  todo: {
    module: 'todo',
    buttonLabel: 'AI for oppgaver',
    icon: 'SparklesIcon',
    contextFields: {
      task_title: { label: 'Tittel', type: 'string', description: 'Oppgavetittel' },
      task_description: { label: 'Beskrivelse', type: 'string', description: 'Beskrivelse' },
      open_tasks: { label: 'Åpne oppgaver', type: 'string', description: 'Liste' },
      hours_available: { label: 'Timer i dag', type: 'string', description: 'Tilgjengelig tid' }
    },
    generatableFields: {
      task_suggestions: {
        label: 'Oppgaveforslag',
        type: 'string',
        description: 'Nye forslag',
        default: true
      },
      time_estimate: {
        label: 'Tidsestimat (timer)',
        type: 'string',
        description: 'Estimat for hovedoppgaven',
        default: false
      },
      daily_plan: {
        label: 'Dagsplan',
        type: 'string',
        description: 'Prioritert rekkefølge',
        default: false
      }
    },
    options: {
      language: {
        label: 'Språk',
        type: 'select',
        options: [
          { value: 'no', label: 'Norsk' },
          { value: 'en', label: 'Engelsk' }
        ],
        default: 'no'
      },
      length: {
        label: 'Lengde',
        type: 'select',
        options: [
          { value: 'short', label: 'Kort' },
          { value: 'medium', label: 'Middels' }
        ],
        default: 'short'
      }
    }
  }
};

/**
 * AI Provider information
 */
export const aiProviders = {
  grok: {
    name: 'Grok AI',
    label: 'Grok AI (xAI)',
    description: 'Krever credits/licenses - ikke gratis for API-bruk',
    cost: 'Betal',
    recommended: false,
    icon: 'SparklesIcon',
    color: 'text-yellow-600',
    warning: 'Bruk av Grok AI koster penger basert på tokens brukt'
  },
  chatgpt: {
    name: 'ChatGPT',
    label: 'ChatGPT (OpenAI)',
    description: 'Standard AI-leverandør - allerede konfigurert',
    cost: 'Betal',
    recommended: true,
    icon: 'ChatBubbleLeftRightIcon',
    color: 'text-blue-600',
    warning: 'Bruk av ChatGPT koster penger basert på tokens brukt'
  }
};
